import { z } from 'zod';
import { paginatedSchema, paginationQuerySchema } from './api-error.js';
import { sortDirectionSchema } from './sort.js';
import { slugSchema } from './client.js';

/**
 * Alert severities. `critical` mentions the channel, `warning` posts quietly,
 * `info` stays in the dashboard and is never posted.
 *
 * @since 1.0.0
 */
export const ALERT_SEVERITIES = ['critical', 'warning', 'info'] as const;

export const alertSeveritySchema = z.enum(ALERT_SEVERITIES);

export type AlertSeverity = z.infer<typeof alertSeveritySchema>;

/**
 * Ranks a severity so escalation can only ever move upward.
 *
 * @since 1.0.0
 * @param severity - the severity to rank
 * @returns a comparable rank where higher means more severe
 */
export function severityRank(severity: AlertSeverity): number {
    return ALERT_SEVERITIES.length - ALERT_SEVERITIES.indexOf(severity);
}

/**
 * What kind of notification an alert delivery represents.
 *
 * @since 1.0.0
 */
export const ALERT_DELIVERY_KINDS = ['open', 'escalate', 'recovery'] as const;

export const alertDeliveryKindSchema = z.enum(ALERT_DELIVERY_KINDS);

export type AlertDeliveryKind = z.infer<typeof alertDeliveryKindSchema>;

export const alertSchema = z.object({
    id: z.number().int(),
    clientId: z.number().int(),
    clientSlug: z.string(),
    clientName: z.string(),
    checkSlug: z.string(),
    fingerprint: z.string(),
    severity: alertSeveritySchema,
    title: z.string(),
    openedRunId: z.string(),
    latestRunId: z.string(),
    occurrences: z.number().int(),
    openedAt: z.number().int(),
    lastSeenAt: z.number().int(),
    resolvedAt: z.number().int().nullable(),
    resolvedBy: z.string().nullable(),
});

export type Alert = z.infer<typeof alertSchema>;

/**
 * Longest alert title a check may raise; it becomes the first line of a Slack message.
 *
 * @since 1.3.0
 */
export const ALERT_TITLE_MAX_LENGTH = 200;

/**
 * Longest fingerprint a check may use to tell one issue from another.
 *
 * @since 1.3.0
 */
export const FINGERPRINT_MAX_LENGTH = 255;

/**
 * One page of alerts, plus how many days back the resolved list reaches.
 *
 * @since 1.3.0
 */
export const alertsPageSchema = paginatedSchema(alertSchema).extend({
    windowDays: z.number().int(),
});

export type AlertsPage = z.infer<typeof alertsPageSchema>;

/**
 * Columns an alert list may be sorted on, named after the table's own column keys. The open
 * and resolved tables each render a subset; the worker accepts the union so one endpoint
 * serves both.
 *
 * @since 1.4.0
 */
export const ALERT_SORT_KEYS = ['severity', 'client', 'title', 'check', 'opened', 'closed', 'duration', 'by'] as const;

export const alertSortKeySchema = z.enum(ALERT_SORT_KEYS);

export type AlertSortKey = z.infer<typeof alertSortKeySchema>;

export const alertQuerySchema = paginationQuerySchema.extend({
    // left optional so the endpoint can default the open list to when an issue opened and
    // the resolved list to when it closed, which is the column each table leads with
    sort: alertSortKeySchema.optional(),
    dir: sortDirectionSchema.default('desc'),
    state: z.enum(['open', 'resolved']).default('open'),
    client: z.string().optional(),
    severity: alertSeveritySchema.optional(),
});

export type AlertQuery = z.infer<typeof alertQuerySchema>;

/**
 * A request to open or update an alert. Checks hand this to the alert engine and
 * never talk to Slack themselves.
 *
 * @since 1.0.0
 */
export const alertSignalSchema = z.object({
    clientId: z.number().int(),
    checkSlug: z.string(),
    fingerprint: z.string(),
    severity: alertSeveritySchema,
    title: z.string().min(1).max(ALERT_TITLE_MAX_LENGTH),
    qcRunId: z.string(),
});

export type AlertSignal = z.infer<typeof alertSignalSchema>;

export const muteRuleSchema = z.object({
    id: z.number().int(),
    clientId: z.number().int().nullable(),
    clientSlug: z.string().nullable(),
    checkSlug: z.string().nullable(),
    fingerprint: z.string().nullable(),
    reason: z.string(),
    createdBy: z.number().int(),
    createdAt: z.number().int(),
    expiresAt: z.number().int().nullable(),
});

export type MuteRule = z.infer<typeof muteRuleSchema>;

/**
 * One page of mute rules.
 *
 * @since 1.3.0
 */
export const muteRulesPageSchema = paginatedSchema(muteRuleSchema);

export type MuteRulesPage = z.infer<typeof muteRulesPageSchema>;

export const createMuteRuleRequestSchema = z.object({
    clientSlug: slugSchema.nullable().default(null),
    checkSlug: slugSchema.nullable().default(null),
    fingerprint: z.string().min(1).max(FINGERPRINT_MAX_LENGTH).nullable().default(null),
    reason: z.string().min(1).max(500),
    expiresAt: z.number().int().nullable().default(null),
});

export type CreateMuteRuleRequest = z.infer<typeof createMuteRuleRequestSchema>;
